import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    AudioPlayer,
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay1,
    LabelDisplay2,
    LabelTelescope,
    SecondaryButton,
    Spacer,
    Textarea,
    Tip,
} from '../../components';
import {
    IllustrationBubbles,
    IllustrationBulb,
    IllustrationWeightLifter,
} from '../../icons';
import { ExerciseDto, ExerciseStatus, Question } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseViewProps } from '../exercises';

const recapInputKey = 'recap';
const goalInputKey = 'goals';
export const dataKeys = [recapInputKey, goalInputKey];
export const questions: Question[] = [
    createQuestionFromConfiguration({
        title: 'Hvordan gik det?',
        description:
            'Var der noget tidspunkt, hvor det kunne lade sig gøre at placere dine tanker?',
        dataKeys: [recapInputKey],
    }),
    createQuestionFromConfiguration({
        title: 'Skriv din målsætning',
        description: (
            <>
                At gøre det konkret er en hjælp til dig selv og øger chancen
                for, at du husker det. Det kan være hvor eller hvornår, du vil
                gøre det.
                <br />
                <br />
                Når jeg går i seng, vil jeg lytte til lydklippet igen
            </>
        ),
        dataKeys: [goalInputKey],
    }),
];

export function Module1S2Exercise2({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Sæbebobler">
            <Page />
        </Exercise>
    );

    function Page() {
        switch (exercise.CurrentPage) {
            default:
            case 1:
                return (
                    <Page1
                        exercise={exercise}
                        savedInput={exercise.Data[recapInputKey] ?? ''}
                        onSave={(input) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                                data: {
                                    [recapInputKey]: normalizeInput(input),
                                },
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        onFinish={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                            });
                        }}
                    />
                );
            case 3:
                return (
                    <Page3
                        savedInput={exercise.Data[goalInputKey] ?? ''}
                        onSkip={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                        onSave={(input) => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                                data: {
                                    [goalInputKey]: normalizeInput(input),
                                },
                            });
                        }}
                    />
                );
        }
    }
}

type Page1Props = {
    exercise: ExerciseDto;
    savedInput: string;
    onSave: (input: string) => void;
};
function Page1({ exercise, savedInput, onSave }: Page1Props) {
    const [input, setInput] = useSyncedState(savedInput);
    return (
        <>
            <LabelDisplay2 class="mb-10">
                Lad dine tanker svæve rundt inde i sæbebobler
            </LabelDisplay2>
            <LabelBody>
                Der er flere måder at skabe afstand mellem dig og dine tanker.
                Her er en mindfulness øvelse, der vil guide dig. Find et roligt
                sted og tryk på afspil, når du er klar.
            </LabelBody>
            <AudioPlayer
                exercise={exercise}
                duration="00:06:36"
                src="https://static.hejdoktor.dk/falcksundtsind/SÆBEBOBLER+02+MIX.mp3"
                type="audio/mpeg"
                className="my-30"
            >
                <LabelBodySmall>Mindfulness</LabelBodySmall>
                <LabelBodyHeadline>Sæbebobler</LabelBodyHeadline>
            </AudioPlayer>
            <IllustrationBubbles className="mx-auto" />
            <Card class="my-30">
                <LabelDisplay1>Hvordan gik det?</LabelDisplay1>
                <LabelBody>
                    Var der noget tidspunkt, hvor det kunne lade sig gøre at
                    placere dine tanker?
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={input}
                    onChangeText={setInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={() => onSave(input)}>
                fortsæt
            </SecondaryButton>
        </>
    );
}

type Page2Props = {
    onContinue: () => void;
    onFinish: () => void;
};
function Page2({ onContinue, onFinish }: Page2Props) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                Når tanken placeres i bobler, bliver du gjort opmærksom på, at
                tanken bare er én ud af mange. Det er et redskab til at lade
                tanken være, hvis den ikke er relevant for dig.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <LabelDisplay2 class="mb-10">Mod på mere?</LabelDisplay2>
            <LabelBody>
                Det er godt at øve sig et par gange i hverdagen. Hvis du kunne
                lide lydklippet, kan du sætte dig et mål om at øve dig i at få
                dine tanker på afstand.
            </LabelBody>
            <Spacer />
            <SecondaryButton class="mb-20 mt-30" onClick={onContinue}>
                Fortsæt
            </SecondaryButton>
            <GhostButton onClick={onFinish}>Afslut øvelsen</GhostButton>
        </>
    );
}

type Page3Props = {
    savedInput: string;
    onSave: (input: string) => void;
    onSkip: () => void;
};
function Page3({ savedInput, onSave, onSkip }: Page3Props) {
    const [input, setInput] = useSyncedState(savedInput);
    return (
        <>
            <LabelDisplay2 class="mb-10">Sæt dig et mål</LabelDisplay2>
            <LabelBody>
                Du kan øve dig i at reagere anderledes på dine tanker ved at
                gentage øvelsen.
            </LabelBody>
            <LabelBody>
                Hør lyd klippet igen på et bestemt tidspunkt eller sted. F.eks.
                når du har lagt dig i sengen om aftenen.
            </LabelBody>
            <IllustrationWeightLifter className="mx-auto my-30" />
            <Card>
                <LabelDisplay1 class="mb-10">
                    Skriv din målsætning
                </LabelDisplay1>
                <LabelBody class="mb-10">
                    At gøre det konkret er en hjælp til dig selv og øger chancen
                    for, at du husker det. Det kan være hvor eller hvornår, du
                    vil gøre det.
                </LabelBody>
                <LabelTelescope>
                    Når jeg går i seng, vil jeg lytte til lydklippet igen
                </LabelTelescope>

                <Textarea
                    className="my-30"
                    placeholder="Skriv her..."
                    text={input}
                    onChangeText={setInput}
                />
                <Tip>
                    Du bestemmer selv, om det er sæbebobler, du vil se for dig,
                    eller om der er noget, du bedre kan lide. Det kan f.eks.
                    være tog, der kører ud og ind, eller sushi på et sushibånd.
                </Tip>
            </Card>
            <Spacer />
            <SecondaryButton class="mb-20 mt-30" onClick={() => onSave(input)}>
                Gem i logbogen
            </SecondaryButton>
            <GhostButton onClick={() => onSkip()}>Spring over</GhostButton>
        </>
    );
}
